import { motion } from "framer-motion";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, Clock, Quote, CheckCircle2 } from "lucide-react";
import { PROJECTS } from "../data/mock";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

export default function CaseStudy() {
  const { slug } = useParams();
  const idx = PROJECTS.findIndex((p) => p.slug === slug);
  if (idx === -1) return <Navigate to="/portfolio" replace />;
  const p = PROJECTS[idx];
  const next = PROJECTS[(idx + 1) % PROJECTS.length];

  return (
    <div data-testid="case-study-page" className="pt-16" key={slug}>
      {/* Hero */}
      <section className="bg-[#FDFBF7] pt-16 pb-12">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
            <Link to="/portfolio" data-testid="case-study-back-link"
              className="inline-flex items-center gap-1.5 text-sm text-[#5C4B51] hover:text-[#6C0B1B] transition-colors duration-200 mb-8">
              <ArrowLeft size={15} /> All case studies
            </Link>
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="rounded-full bg-[#6C0B1B] text-white text-xs font-medium px-3.5 py-1.5">{p.service}</span>
              <span className="rounded-full border border-[#6C0B1B]/20 text-[#6C0B1B] text-xs font-medium px-3.5 py-1.5 flex items-center gap-1.5">
                <Clock size={12} /> {p.duration}
              </span>
              <span className="text-xs text-[#A31621] font-semibold uppercase tracking-wider">{p.industry}</span>
            </div>
            <h1 data-testid="case-study-title" className="font-heading text-5xl sm:text-6xl font-light tracking-tight leading-[1.05] text-[#1A1014] max-w-3xl">
              {p.client}
            </h1>
            <p className="mt-5 text-lg text-[#5C4B51] leading-relaxed max-w-2xl">{p.summary}</p>
          </motion.div>
        </div>
      </section>

      {/* Main image */}
      <section className="bg-[#FDFBF7] pb-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.img {...fadeUp} src={p.image} alt={p.client}
            className="w-full h-[320px] sm:h-[480px] object-cover rounded-2xl shadow-[0_30px_60px_-20px_rgba(108,11,27,0.25)]" />
        </div>
      </section>

      {/* Story + results */}
      <section className="bg-[#F3EFE9] py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <motion.div {...fadeUp}>
              <p className="uppercase text-xs tracking-[0.2em] text-[#A31621] font-semibold mb-3">The Challenge</p>
              <p className="text-lg text-[#1A1014] leading-relaxed">{p.challenge}</p>
            </motion.div>
            <motion.div {...fadeUp} className="mt-12">
              <p className="uppercase text-xs tracking-[0.2em] text-[#A31621] font-semibold mb-5">What We Did</p>
              <ul className="space-y-4">
                {p.approach.map((step) => (
                  <li key={step} className="flex gap-3.5 items-start">
                    <CheckCircle2 size={19} className="text-[#6C0B1B] shrink-0 mt-1" />
                    <p className="text-[#1A1014] leading-relaxed">{step}</p>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div {...fadeUp} className="mt-12">
              <p className="uppercase text-xs tracking-[0.2em] text-[#A31621] font-semibold mb-3">The Outcome</p>
              <p className="text-lg text-[#1A1014] leading-relaxed">{p.outcome}</p>
            </motion.div>
          </div>
          <div className="lg:col-span-5">
            <motion.div {...fadeUp} data-testid="case-study-results-panel"
              className="lg:sticky lg:top-24 bg-white border border-[#6C0B1B]/10 rounded-2xl p-8 shadow-[0_20px_40px_-15px_rgba(108,11,27,0.1)]">
              <h3 className="font-heading text-2xl font-semibold text-[#1A1014]">Results at a glance</h3>
              <div className="mt-5">
                {p.results.map((r) => (
                  <div key={r.metric} className="py-4 border-b border-[#6C0B1B]/8 last:border-0">
                    <p className="text-sm text-[#5C4B51]">{r.metric}</p>
                    <div className="flex items-center gap-3 mt-1.5">
                      <span className="text-base text-[#5C4B51]/70 line-through decoration-[#A31621]/40">{r.before}</span>
                      <ArrowRight size={15} className="text-[#A31621]" />
                      <span className="font-heading text-3xl font-semibold text-[#6C0B1B]">{r.after}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 rounded-xl bg-[#6C0B1B] text-white p-6">
                <Quote size={22} className="text-[#D4A24C]" />
                <p className="mt-3 text-[15px] leading-relaxed text-white/90">"{p.quote.text}"</p>
                <p className="mt-3 text-sm font-semibold text-[#D4A24C]">— {p.quote.author}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="bg-[#FDFBF7] py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="mb-10">
            <p className="uppercase text-xs tracking-[0.2em] text-[#A31621] font-semibold mb-3">Project Gallery</p>
            <h2 className="font-heading text-4xl font-medium tracking-tight text-[#1A1014]">A closer look</h2>
          </motion.div>
          <div data-testid="case-study-gallery" className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {p.gallery.map((img, i) => (
              <motion.div key={img} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }}
                className="group overflow-hidden rounded-2xl border border-[#6C0B1B]/10">
                <img src={img} alt={`${p.client} gallery ${i + 1}`}
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-700" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Next project + CTA */}
      <section className="bg-[#FDFBF7] pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="rounded-2xl bg-[#1A1014] text-white overflow-hidden grid grid-cols-1 md:grid-cols-2">
            <Link to={`/portfolio/${next.slug}`} data-testid="case-study-next-link"
              className="group p-10 sm:p-12 border-b md:border-b-0 md:border-r border-white/10 hover:bg-white/5 transition-colors duration-200">
              <p className="uppercase text-[10px] tracking-[0.25em] text-[#D4A24C] font-semibold">Next case study</p>
              <p className="mt-3 font-heading text-3xl sm:text-4xl font-light flex items-center gap-3">
                {next.client} <ArrowRight size={22} className="text-[#D4A24C] group-hover:translate-x-1.5 transition-transform duration-200" />
              </p>
              <p className="mt-2 text-sm text-white/60">{next.industry}</p>
            </Link>
            <div className="p-10 sm:p-12 flex flex-col justify-center items-start">
              <p className="font-heading text-2xl sm:text-3xl font-light">Want results like these?</p>
              <Link to="/contact" data-testid="case-study-contact-cta"
                className="inline-flex items-center gap-2 mt-5 rounded-full bg-[#6C0B1B] text-white px-7 py-3.5 font-medium hover:bg-[#8C1126] transition-colors duration-200">
                Start Your Project <ArrowUpRight size={17} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
